'use client'
import React, { useEffect, useRef, useImperativeHandle, useState } from 'react'
import { gsap } from 'gsap'
import { SplitText } from 'gsap/dist/SplitText'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { cn } from '../../helpers/utils.js'
import { cva } from 'class-variance-authority'
import { Heading, Text, Countdown } from '../../components/index.js'
import { withLayout } from '../../shared/Styles.js'
import type { TitlePropsBase } from './types.js'

export interface TitleProps extends TitlePropsBase, Omit<React.HTMLAttributes<HTMLDivElement>, 'className' | 'title'> {}
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import { useInView } from 'react-intersection-observer'

const componentType = 'link'
const componentName = 'title'

/**
 * 标题样式变体
 */
const titleHeadingVariants = cva('', {
  variants: {
    theme: {
      light: 'text-[#080A0F]',
      dark: 'text-[#F5F6F7]',
    },
  },
  defaultVariants: {
    theme: 'light',
  },
})

/**
 * 副标题样式变体
 */
const subtitleVariants = cva(
  'desktop:text-base desktop:mt-2 lg-desktop:text-[18px] mt-1 text-[14px] font-bold leading-[1.4] tracking-[-0.36px]',
  {
    variants: {
      theme: {
        light: 'text-[#080A0F]',
        dark: 'text-[#F5F6F7]',
      },
    },
    defaultVariants: {
      theme: 'light',
    },
  }
)

const TitleButton = ({ data, className }: { data: TitleProps['data']; className?: string }) => {
  const { theme = 'light', extensions, title, caption, align } = data
  if (!extensions?.textLink) return null
  return (
    <a
      className={cn(
        { 'aiui-dark': theme === 'dark' },
        'hover:text-brand-0 [&_svg_path]:hover:fill-brand-0 lg-desktop:text-base flex items-center overflow-hidden text-sm font-[700] leading-[1.4] transition-all duration-[0.4s]',
        { 'text-[#080A0F]': theme === 'light' },
        { 'text-[#F5F6F7]': theme === 'dark' },
        { 'justify-center mt-4': align === 'center' },
        { 'mt-1 laptop:mt-0': align === 'left' },
        className
      )}
      href={trackUrlRef(extensions?.link, `${componentType}_${componentName}`)}
      data-headless-type-name={`${componentType}#${componentName}`}
      data-headless-title-desc-button={`${title}#${caption}`}
    >
      <div className="truncate whitespace-nowrap">{extensions?.textLink}</div>
      <div className="lg-desktop:size-5 size-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 16 16" fill="none">
          <path
            d="M5.52827 3.52864C5.78862 3.26829 6.21063 3.26829 6.47098 3.52864L10.471 7.52864C10.7313 7.78899 10.7313 8.21099 10.471 8.47134L6.47098 12.4713C6.21063 12.7317 5.78862 12.7317 5.52827 12.4713C5.26792 12.211 5.26792 11.789 5.52827 11.5286L9.05692 7.99999L5.52827 4.47134C5.26792 4.21099 5.26792 3.78899 5.52827 3.52864Z"
            fill={theme === 'dark' ? '#F5F6F7' : '#080A0F'}
            className="transition-all duration-[0.4s]"
          />
        </svg>
      </div>
    </a>
  )
}

const Title = React.forwardRef<HTMLDivElement, TitleProps>(
  ({ data, className, as = 'h2', weight = 'bold', ...rest }, ref) => {
    const {
      title,
      caption,
      subtitle,
      countdown,
      showCountdown = false,
      theme = 'light',
      extensions,
      align = 'left',
    } = data
    const innerRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<HTMLHeadingElement>(null)
    const splitTextInstance = useRef<SplitText | null>(null)
    const scrollTriggerRef = useRef<ScrollTrigger | null>(null)

    // 控制倒计时显示状态
    const [isCountdownVisible, setIsCountdownVisible] = useState(true)

    const { ref: inViewRef, inView } = useInView()

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

    // 倒计时结束回调
    const handleCountdownEnd = () => {
      setIsCountdownVisible(false)
    }

    // CMS field mapping: new targetDateTime > legacy targetDate
    const countdownEndDate = countdown?.targetDateTime || countdown?.targetDate || ''
    const countdownTz = countdown?.targetDateTime_tz
    // Label mapping: plural keys (Title CMS format) → atomic Countdown format
    const countdownLabels = countdown?.labels
      ? {
          day: countdown.labels.days || 'Day',
          hour: countdown.labels.hours || 'Hours',
          minute: countdown.labels.minutes || 'Mins',
          second: countdown.labels.seconds || 'Secs',
        }
      : undefined

    useEffect(() => {
      gsap.registerPlugin(SplitText, ScrollTrigger)
      function gsapResize() {
        if (!titleRef.current) return
        const height = titleRef.current?.clientHeight || 80
        if (splitTextInstance.current) {
          splitTextInstance.current.revert()
        }
        if (scrollTriggerRef.current) {
          scrollTriggerRef.current.kill()
        }
        splitTextInstance.current = new SplitText(titleRef.current, {
          type: 'words',
          wordsClass: 'word',
        })
        const words = splitTextInstance.current.words
        gsap.set(words, { opacity: 0 })
        scrollTriggerRef.current = ScrollTrigger.create({
          trigger: titleRef.current,
          start: 'bottom bottom-=4%',
          end: `bottom+=${height * 1.5 + 60}px bottom-=4%`,
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: (self: any) => {
            const progress = self.progress
            const total = words.length || 1
            const overlap = 0.5
            const interval = 1 / total
            const step = interval * (1 - overlap)
            const lastEnd = (total - 1) * step + interval
            const normalizedProgress = Math.min(1, lastEnd > 0 ? progress / lastEnd : 0)
            words.forEach((word: any, i: number) => {
              const start = i * step
              const width = interval
              let opacity = (normalizedProgress - start) / width
              opacity = Math.max(0, Math.min(1, opacity))
              gsap.set(word, { opacity })
            })
          },
        })
      }

      if (inView) {
        gsapResize()
      }

      return () => {
        splitTextInstance.current && splitTextInstance.current.revert()
        // ScrollTrigger.getAll().forEach((t: { kill: () => any }) => t.kill())
        scrollTriggerRef.current && scrollTriggerRef.current.kill()
      }
    }, [inView])

    return (
      <div
        {...rest}
        id={extensions?.id}
        className={cn('titleBottom title-box flex items-end justify-between gap-2 pb-6', className)}
        ref={innerRef}
      >
        <div
          ref={inViewRef}
          className={cn('flex-1', className, {
            'aiui-dark': theme === 'dark',
            'text-center': align === 'center',
            'text-left': align === 'left',
          })}
        >
          {(caption || title) && (
            <Heading
              ref={titleRef}
              as={as}
              size={4}
              html={caption || title}
              weight={weight}
              className={titleHeadingVariants({ theme })}
            />
          )}
          {subtitle && <Text html={subtitle} as="p" className={subtitleVariants({ theme })} />}
          <TitleButton data={data} className={cn({ 'laptop:hidden': align === 'left' })} />
          {showCountdown && countdown && isCountdownVisible && (
            <Countdown
              endDate={countdownEndDate}
              endDate_tz={countdownTz}
              timeLabels={countdownLabels}
              showDays={countdown?.showDays}
              showHours={countdown?.showHours}
              showMinutes={countdown?.showMinutes}
              showSeconds={countdown?.showSeconds}
              theme={theme}
              onExpire={handleCountdownEnd}
              hideWhenExpired={true}
              align={align === 'center' ? 'center' : 'left'}
              className={cn('mt-4')}
            />
          )}
        </div>
        <TitleButton data={data} className={cn('hidden', { ['laptop:flex']: align === 'left' })} />
      </div>
    )
  }
)

Title.displayName = 'Title'

export default withLayout(Title)
